import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { adminQueries, keys } from '@blimo/api-client';
import { useApi } from '@blimo/api-client/react';
import { unitEditSchema } from '@blimo/shared';
import {
  applyProblemToForm,
  Button,
  Form,
  FormRootError,
  Sheet,
  SheetContent,
  SheetDescription,
  SheetFooter,
  SheetHeader,
  SheetTitle,
  SelectField,
  TextareaField,
  toast,
} from '@blimo/ui';
import { ReadOnlyHint } from '../../components/data.jsx';

/**
 * Edits the BLIMO-owned fields of a unit (PATCH /admin/units/{id}, `units.update`): type (G-18, list from
 * GET /admin/unit-types) and notes. The type is only sent when it was changed – editing the notes never touches it
 * (Altportal-Fehler F-120: Bearbeiten setzte den Typ zurück; Regressionstest PropertyImageAndEditingTest).
 * @param {{ unit: any, open: boolean, onOpenChange: (open: boolean) => void }} props
 */
export function UnitEditSheet({ unit, open, onOpenChange }) {
  return (
    <Sheet open={open} onOpenChange={onOpenChange}>
      <SheetContent>
        <SheetHeader>
          <SheetTitle>Einheit bearbeiten</SheetTitle>
          <SheetDescription>{[unit.number, unit.designation].filter(Boolean).join(' · ') || `Einheit #${unit.id}`}</SheetDescription>
        </SheetHeader>
        {open && <UnitEditForm unit={unit} onDone={() => onOpenChange(false)} />}
      </SheetContent>
    </Sheet>
  );
}

function UnitEditForm({ unit, onDone }) {
  const api = useApi();
  const queryClient = useQueryClient();
  const types = useQuery(adminQueries.unitTypes(api));
  const form = useForm({
    resolver: zodResolver(unitEditSchema),
    defaultValues: { notes: unit.notes ?? '', unit_type_id: unit.type?.id ? String(unit.type.id) : '' },
  });
  const mutation = useMutation({
    mutationFn: (values) => {
      const payload = { notes: values.notes?.trim() || null };
      if (form.formState.dirtyFields.unit_type_id) payload.unit_type_id = values.unit_type_id ? Number(values.unit_type_id) : null;
      return api.admin.units.update(unit.id, payload);
    },
    onSuccess: (updated) => {
      // PATCH answers without ownerships/tenancies – merge instead of replacing the detail.
      queryClient.setQueryData(keys.admin.detail('units', unit.id), (previous) => ({ ...previous, ...updated }));
      queryClient.invalidateQueries({ queryKey: ['blimo', 'admin', 'units', 'list'] });
      toast.success('Einheit gespeichert');
      onDone();
    },
  });

  const onSubmit = form.handleSubmit(async (values) => {
    try {
      await mutation.mutateAsync(values);
    } catch (error) {
      applyProblemToForm(error, form.setError, { fields: ['notes', 'unit_type_id'] });
    }
  });

  return (
    <Form {...form}>
      <form onSubmit={onSubmit} className="flex flex-1 flex-col gap-4 px-4" noValidate>
        <ReadOnlyHint>Nummer, Bezeichnung und Wohnfläche werden aus WinCasa übernommen und können hier nicht geändert werden.</ReadOnlyHint>
        <FormRootError />
        <SelectField
          name="unit_type_id"
          label="Typ"
          placeholder={types.isPending ? 'Typen werden geladen …' : 'Ohne Typ'}
          options={(types.data ?? []).map((t) => ({ value: String(t.id), label: t.label }))}
          description={
            unit.source_type_label
              ? `Laut WinCasa: ${unit.source_type_label}. Hinweis: Der WinCasa-Abgleich setzt den Typ derzeit auf den Quellwert zurück.`
              : undefined
          }
        />
        <TextareaField name="notes" label="Notizen" rows={8} description="Interne Notiz zur Einheit (max. 5.000 Zeichen)." />
        <SheetFooter className="px-0">
          <Button type="submit" loading={form.formState.isSubmitting}>
            Speichern
          </Button>
          <Button type="button" variant="outline" onClick={onDone}>
            Abbrechen
          </Button>
        </SheetFooter>
      </form>
    </Form>
  );
}
